import { Badge, Button, cn } from '@parcel/ui';
import { Check, ChevronLeft, Minus } from 'lucide-react';
import type { BillingPlan, BillingPlanDefinition, PaidBillingPlan } from '../../lib/billing.js';
import {
  type PlanComparisonValue,
  getPlanCardState,
  getPlanChangeLabel,
  isPlanUpgrade,
  planComparison,
} from './billing-settings.js';

const GRID = 'grid grid-cols-[minmax(11rem,1.2fr)_repeat(3,minmax(0,1fr))]';

function ComparisonValue({ value }: { value: PlanComparisonValue }) {
  if (value === false) {
    return <Minus className="size-4 text-muted-foreground/60" aria-label="Not included" />;
  }
  return (
    <span className="flex items-center gap-2">
      <Check className="size-4 shrink-0 text-brand" aria-label="Included" />
      {typeof value === 'string' ? <span>{value}</span> : null}
    </span>
  );
}

/** Every plan side by side, opened from Billing's All plans link; Back returns to Billing. */
export function BillingPlansView({
  plans,
  currentPlan,
  canManageBilling,
  hasStripeSubscription,
  pendingPlan,
  openingPortal,
  spec,
  onBack,
  onCheckout,
  onOpenPortal,
}: {
  plans: BillingPlanDefinition[];
  currentPlan: BillingPlan;
  canManageBilling: boolean;
  hasStripeSubscription: boolean;
  pendingPlan: PaidBillingPlan | null;
  openingPortal: boolean;
  /** Whether Spec rows (search limits, AI seats) belong in the comparison. */
  spec: boolean;
  onBack: () => void;
  onCheckout: (plan: BillingPlan) => void;
  onOpenPortal: () => void;
}) {
  const sections = planComparison({ spec });
  const currentName = plans.find((plan) => plan.plan === currentPlan)?.name ?? null;
  // The current plan's column carries the brand tint so the reader can find themselves.
  const shade = (plan: BillingPlan) => (plan === currentPlan ? 'bg-brand-muted/60' : null);

  return (
    <div data-testid="billing-plans-view">
      <button
        type="button"
        onClick={onBack}
        className="-ml-1 mb-6 inline-flex items-center gap-1 rounded-md px-1 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Billing
      </button>
      <h2 className="text-3xl font-semibold tracking-tight">Plans</h2>
      {currentName ? (
        <p className="mt-2 text-sm text-muted-foreground">
          You are on the <span className="font-medium text-foreground">{currentName}</span> plan.
        </p>
      ) : null}

      <div className="mt-8 overflow-x-auto">
        <div className="min-w-[720px] text-sm">
          <div className={GRID}>
            <div />
            {plans.map((plan) => {
              const state = getPlanCardState({
                plan: plan.plan,
                currentPlan,
                canManageBilling,
                hasStripeSubscription,
              });
              const label = getPlanChangeLabel({ plan: plan.plan, currentPlan, canManageBilling });
              const actionable = state.canStartCheckout || state.canOpenPortal;
              const pending = pendingPlan === plan.plan;
              const upgrade = label === 'Upgrade';
              return (
                <div
                  key={plan.plan}
                  data-testid={`plan-column-${plan.plan}`}
                  className={cn('space-y-3 rounded-t-lg px-4 pb-5 pt-5', shade(plan.plan))}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{plan.name}</h3>
                    {/* Only on an upgrade: a Pro workspace is not nudged toward Starter. */}
                    {plan.featured && isPlanUpgrade(plan.plan, currentPlan) ? (
                      <Badge variant="secondary">Most popular</Badge>
                    ) : null}
                  </div>
                  <p>
                    <span className="text-base font-semibold tabular-nums">{plan.price}</span>{' '}
                    <span className="text-muted-foreground">per {plan.interval}</span>
                  </p>
                  <Button
                    type="button"
                    className="w-full"
                    variant={upgrade && actionable ? 'default' : 'outline'}
                    disabled={!actionable || Boolean(pendingPlan) || openingPortal}
                    onClick={() => {
                      if (state.canOpenPortal) onOpenPortal();
                      else onCheckout(plan.plan);
                    }}
                  >
                    {pending
                      ? 'Opening Checkout...'
                      : state.canOpenPortal && openingPortal
                        ? 'Opening portal...'
                        : label}
                  </Button>
                </div>
              );
            })}
          </div>

          {sections.map((section, sectionIndex) => (
            <div key={section.title}>
              <div className={GRID}>
                <h4 className="pb-2 pt-6 text-base font-medium">{section.title}</h4>
                {plans.map((plan) => (
                  <div key={plan.plan} className={cn(shade(plan.plan))} />
                ))}
              </div>
              {section.rows.map((row, rowIndex) => {
                const last =
                  sectionIndex === sections.length - 1 && rowIndex === section.rows.length - 1;
                return (
                  <div key={row.label} className={GRID} data-testid="plan-comparison-row">
                    <div className="border-t py-3 pr-4 text-muted-foreground">{row.label}</div>
                    {plans.map((plan) => (
                      <div
                        key={plan.plan}
                        className={cn(
                          'border-t px-4 py-3',
                          shade(plan.plan),
                          last && 'rounded-b-lg',
                        )}
                      >
                        <ComparisonValue value={row.values[plan.plan]} />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
